import { useMemo } from 'react';
import { Contract, JsonRpcProvider } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { ADDRESSES } from '../config/contracts';
import { ACTIVE_CHAIN } from '../config/network';
import GRX_LOGIC_ABI from '../config/abis/GRXPlanLogic.json';
import GRX_STORE_ABI from '../config/abis/GRXStorageCore.json';
import GRX_VIEWS_ABI from '../config/abis/GRXPlanViews.json';
import GRX_ADMIN_ABI from '../config/abis/GRXPlanAdmin.json';
import GRX_REWARDS_ABI from '../config/abis/GRXPlanRewards.json';
import PINK_LOCK_ABI from '../config/abis/PinkLock.json';
import ROUTER_ABI from '../config/abis/PancakeRouter.json';
import ERC20_ABI from '../config/abis/ERC20.json';

const isZero = (a) => !a || a === '0x0000000000000000000000000000000000000000';

let cachedRpc = null;
const getReadProvider = () => {
  if (cachedRpc) return cachedRpc;
  cachedRpc = new JsonRpcProvider(ACTIVE_CHAIN.rpcUrls[0], ACTIVE_CHAIN.chainId);
  return cachedRpc;
};

export const useContracts = () => {
  const { signer, isRightNetwork } = useWallet();

  return useMemo(() => {
    const readProv = getReadProvider();
    const writeSigner = signer && isRightNetwork ? signer : null;

    const logicRead    = new Contract(ADDRESSES.planLogic, GRX_LOGIC_ABI, readProv);
    const storeRead    = new Contract(ADDRESSES.storage,   GRX_STORE_ABI, readProv);
    const viewsRead    = new Contract(ADDRESSES.planViews, GRX_VIEWS_ABI, readProv);
    const adminRead    = isZero(ADDRESSES.planAdmin) ? null : new Contract(ADDRESSES.planAdmin, GRX_ADMIN_ABI, readProv);
    const rewardsRead  = isZero(ADDRESSES.planRewards) ? null : new Contract(ADDRESSES.planRewards, GRX_REWARDS_ABI, readProv);
    const pinkLockRead = isZero(ADDRESSES.pinkSaleLocker) ? null : new Contract(ADDRESSES.pinkSaleLocker, PINK_LOCK_ABI, readProv);
    const routerRead   = isZero(ADDRESSES.pancakeRouter) ? null : new Contract(ADDRESSES.pancakeRouter, ROUTER_ABI, readProv);
    const grxRead      = new Contract(ADDRESSES.grx,       ERC20_ABI, readProv);
    const usdtRead     = new Contract(ADDRESSES.usdt,      ERC20_ABI, readProv);

    const logicWrite    = writeSigner ? new Contract(ADDRESSES.planLogic, GRX_LOGIC_ABI, writeSigner) : null;
    const storeWrite    = writeSigner ? new Contract(ADDRESSES.storage,   GRX_STORE_ABI, writeSigner) : null;
    const viewsWrite    = writeSigner ? new Contract(ADDRESSES.planViews, GRX_VIEWS_ABI, writeSigner) : null;
    const adminWrite    = (writeSigner && !isZero(ADDRESSES.planAdmin)) ? new Contract(ADDRESSES.planAdmin, GRX_ADMIN_ABI, writeSigner) : null;
    const rewardsWrite  = (writeSigner && !isZero(ADDRESSES.planRewards)) ? new Contract(ADDRESSES.planRewards, GRX_REWARDS_ABI, writeSigner) : null;
    const pinkLockWrite = (writeSigner && !isZero(ADDRESSES.pinkSaleLocker)) ? new Contract(ADDRESSES.pinkSaleLocker, PINK_LOCK_ABI, writeSigner) : null;
    const routerWrite   = (writeSigner && !isZero(ADDRESSES.pancakeRouter)) ? new Contract(ADDRESSES.pancakeRouter, ROUTER_ABI, writeSigner) : null;
    const grxWrite      = writeSigner ? new Contract(ADDRESSES.grx,       ERC20_ABI, writeSigner) : null;
    const usdtWrite     = writeSigner ? new Contract(ADDRESSES.usdt,      ERC20_ABI, writeSigner) : null;

    return {
      addresses: ADDRESSES,
      hasAdmin: !isZero(ADDRESSES.planAdmin),
      hasRewards: !isZero(ADDRESSES.planRewards),
      hasPinkLock: !isZero(ADDRESSES.pinkSaleLocker),
      hasRouter: !isZero(ADDRESSES.pancakeRouter),
      read:  { logic: logicRead,  store: storeRead,  views: viewsRead, admin: adminRead, rewards: rewardsRead,  pinkLock: pinkLockRead, router: routerRead, grx: grxRead, usdt: usdtRead },
      write: { logic: logicWrite, store: storeWrite, views: viewsWrite, admin: adminWrite, rewards: rewardsWrite, pinkLock: pinkLockWrite, router: routerWrite, grx: grxWrite, usdt: usdtWrite },
    };
  }, [signer, isRightNetwork]);
};
